import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export default function Dictionary() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("dictionary-search", { body: { q: q.trim() } });
      if (error) throw error;
      setResults(data?.data || []);
    } catch (err: any) {
      toast.error(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }

  async function favorite(item: any) {
    if (!user) return;
    const word = item.japanese?.[0]?.word || item.japanese?.[0]?.reading || item.slug;
    const reading = item.japanese?.[0]?.reading;
    const meaning = item.senses?.[0]?.english_definitions?.join(", ");
    const { error } = await supabase.from("dictionary_favorites").insert({
      user_id: user.id, word, reading, meaning, jisho_payload: item,
    });
    if (error) {
      if (error.message.includes("duplicate")) toast.info("Already saved");
      else toast.error(error.message);
    } else {
      toast.success("Saved to favorites");
    }
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Dictionary</h1>
        <p className="text-muted-foreground">Search any Japanese or English word (powered by Jisho).</p>
      </div>
      <form onSubmit={search} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 食べる or eat" />
        <Button type="submit" disabled={loading}>
          <Search className="h-4 w-4 mr-2" /> Search
        </Button>
      </form>
      <div className="space-y-3">
        {results.map((item, i) => {
          const jp = item.japanese?.[0] || {};
          return (
            <Card key={i}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-2xl font-bold">{jp.word || jp.reading}</div>
                    {jp.word && jp.reading && <div className="text-muted-foreground">{jp.reading}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.is_common && <Badge variant="secondary">Common</Badge>}
                    {item.jlpt?.map((j: string) => <Badge key={j}>{j.toUpperCase()}</Badge>)}
                    <Button size="icon" variant="ghost" onClick={() => favorite(item)}>
                      <Heart className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {item.senses?.map((s: any, j: number) => (
                    <div key={j} className="text-sm">
                      <span className="text-muted-foreground">{j + 1}.</span>{" "}
                      {s.english_definitions?.join("; ")}
                      {s.parts_of_speech?.length > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">({s.parts_of_speech.join(", ")})</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!results.length && q && !loading && <p className="text-muted-foreground">No results.</p>}
      </div>
    </div>
  );
}
